/**
 * HMC nav smart search — predictive autocomplete (Shopify /search/suggest.json) in a Kits-style dropdown.
 * Instances: .hmc-smart-search--nav (right nav), --compact (narrow desktop), --drawer (mobile menu).
 */
(function () {
  'use strict';

  var DEBOUNCE_MS = 220;
  var MIN_QUERY = 2;

  function formatMoney(amount) {
    var n = parseFloat(amount, 10);
    if (isNaN(n)) return amount;
    try {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(Math.round(n * 100), window.theme && window.theme.moneyFormat);
      }
    } catch (e) {}
    return '$' + n.toFixed(2);
  }

  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function highlightQuery(title, query) {
    if (!query) return escapeHtml(title);
    var re = new RegExp('(' + query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + ')', 'ig');
    return escapeHtml(title).replace(re, '<mark>$1</mark>');
  }

  function isDesktopSearchViewport() {
    try {
      return window.matchMedia('(min-width: 1000px)').matches;
    } catch (e) {
      return false;
    }
  }

  function SmartSearch(root) {
    this.root = root;
    this.trigger = root.querySelector('.hmc-smart-search__trigger');
    this.panel = root.querySelector('.hmc-smart-search__panel');
    this.form = root.querySelector('.hmc-smart-search__form');
    this.input = root.querySelector('.hmc-smart-search__input');
    this.results = root.querySelector('[data-hmc-ss-results]');
    this.clearBtn = root.querySelector('[data-hmc-ss-clear]');
    this.isDrawer = root.classList.contains('hmc-smart-search--drawer');
    this.open = false;
    this.timer = null;
    this.reqId = 0;
    this.lastQuery = '';

    if (!this.input || !this.results) return;
    this._bind();
  }

  SmartSearch.prototype._bind = function () {
    var self = this;

    if (this.trigger) {
      this.trigger.addEventListener('click', function (e) {
        e.preventDefault();
        if (!self.open) {
          self.openPanel();
        } else if (isDesktopSearchViewport()) {
          self.close();
        } else {
          self._focusInput();
        }
      });
    }

    var field = this.root.querySelector('.hmc-smart-search__field');
    if (field) {
      field.addEventListener('click', function (e) {
        if (e.target.closest('[data-hmc-ss-clear]')) return;
        if (!self.isDrawer && !self.open) {
          self.openPanel();
        } else {
          self._focusInput();
        }
      });
    }

    this.input.addEventListener('input', function () {
      self._onInput();
    });

    this.input.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        self.close();
      }
    });

    if (this.clearBtn) {
      this.clearBtn.addEventListener('click', function () {
        self.input.value = '';
        self._onInput();
        self.input.focus();
      });
    }

    if (this.form) {
      this.form.addEventListener('submit', function (e) {
        if (!self.input.value.trim()) e.preventDefault();
      });
    }

    if (!this.isDrawer) {
      document.addEventListener('click', function (e) {
        if (!self.open) return;
        if (self.root.contains(e.target)) return;
        self.close();
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && self.open) self.close();
      });
      window.addEventListener('resize', function () {
        if (self.open) self._positionPanel();
      });
      window.addEventListener('scroll', function () {
        if (self.open) self._positionPanel();
      }, { passive: true });
    } else {
      /* Drawer: always “open”; results expand inline below the field. */
      this.panel.setAttribute('aria-hidden', 'false');
      this.open = true;
    }
  };

  SmartSearch.prototype.openPanel = function () {
    document.querySelectorAll('.hmc-smart-search.is-open').forEach(function (el) {
      if (el !== this.root) {
        el.classList.remove('is-open');
        var t = el.querySelector('.hmc-smart-search__trigger');
        var p = el.querySelector('.hmc-smart-search__panel');
        if (t) t.setAttribute('aria-expanded', 'false');
        if (p) p.setAttribute('aria-hidden', 'true');
      }
    }.bind(this));

    this.open = true;
    this.root.classList.add('is-open');
    if (this.trigger) this.trigger.setAttribute('aria-expanded', 'true');
    this.panel.setAttribute('aria-hidden', 'false');
    this._positionPanel();
    this._focusInput();
  };

  /**
   * Focus the query field (and show the mobile keyboard). Must run synchronously in the
   * user-gesture handler on iOS; requestAnimationFrame alone is often too late.
   */
  SmartSearch.prototype._focusInput = function () {
    if (!this.input) return;

    var input = this.input;
    var self = this;

    function applyFocus() {
      try {
        input.focus({ preventScroll: true });
      } catch (err) {
        input.focus();
      }
      if (typeof input.setSelectionRange === 'function') {
        var end = input.value.length;
        try {
          input.setSelectionRange(end, end);
        } catch (rangeErr) {}
      }
    }

    applyFocus();

    requestAnimationFrame(function () {
      if (document.activeElement !== input) {
        applyFocus();
      }
      var q = input.value.trim();
      if (q.length >= MIN_QUERY) {
        self._fetch(q);
      }
    });
  };

  SmartSearch.prototype.close = function () {
    if (this.isDrawer) return;
    this.open = false;
    this.root.classList.remove('is-open');
    if (this.trigger) this.trigger.setAttribute('aria-expanded', 'false');
    this.panel.setAttribute('aria-hidden', 'true');
    if (this.input && document.activeElement === this.input) {
      this.input.blur();
    }
  };

  SmartSearch.prototype._positionPanel = function () {
    if (this.isDrawer || !this.trigger) return;
    var r = this.trigger.getBoundingClientRect();
    var panelW = Math.min(420, Math.max(300, window.innerWidth - 32));
    var left = Math.min(Math.max(16, r.right - panelW), window.innerWidth - panelW - 16);
    var top = Math.round(r.bottom + 10);
    this.panel.style.setProperty('--hmc-ss-panel-top', top + 'px');
    this.panel.style.setProperty('--hmc-ss-panel-left', left + 'px');
    this.panel.style.setProperty('--hmc-ss-panel-width', panelW + 'px');
  };

  SmartSearch.prototype._onInput = function () {
    var q = this.input.value.trim();
    if (this.clearBtn) this.clearBtn.hidden = q.length === 0;

    if (this.timer) clearTimeout(this.timer);
    var self = this;
    this.timer = setTimeout(function () {
      self._fetch(q);
    }, DEBOUNCE_MS);
  };

  SmartSearch.prototype._fetch = function (q) {
    var self = this;
    this.lastQuery = q;

    if (q.length < MIN_QUERY) {
      this.results.innerHTML = '';
      this.results.setAttribute('aria-hidden', 'true');
      this.results.classList.remove('is-visible');
      return;
    }

    this.results.setAttribute('aria-hidden', 'false');
    this.results.classList.add('is-visible', 'is-loading');
    this.results.innerHTML =
      '<p class="hmc-smart-search__status">Searching…</p>';

    var reqId = ++this.reqId;
    var unavailable =
      (window.theme && window.theme.searchUnavailableProducts) || 'last';
    var url =
      '/search/suggest.json?q=' +
      encodeURIComponent(q) +
      '&resources[type]=product&resources[limit]=6&resources[options[unavailable_products]]=' +
      encodeURIComponent(unavailable);

    fetch(url, { credentials: 'same-origin' })
      .then(function (res) {
        return res.json();
      })
      .then(function (data) {
        if (reqId !== self.reqId || q !== self.lastQuery) return;
        self._renderResults(q, data);
      })
      .catch(function () {
        if (reqId !== self.reqId) return;
        self._fetchFallback(q, reqId);
      });
  };

  SmartSearch.prototype._fetchFallback = function (q, reqId) {
    var self = this;
    var url =
      (window.routes && window.routes.searchUrl ? window.routes.searchUrl : '/search') +
      '?section_id=search-ajax&q=' +
      encodeURIComponent(q) +
      '&type=product&options[prefix]=last';

    fetch(url, { credentials: 'same-origin' })
      .then(function (res) {
        return res.text();
      })
      .then(function (html) {
        if (reqId !== self.reqId) return;
        self.results.classList.remove('is-loading');
        var wrap = document.createElement('div');
        wrap.innerHTML = html;
        var inner = wrap.querySelector('.search-bar__results-inner') || wrap.querySelector('.search-ajax');
        if (inner) {
          self.results.innerHTML = inner.innerHTML || inner.outerHTML;
        } else {
          self.results.innerHTML =
            '<p class="hmc-smart-search__empty">No results found.</p>';
        }
      })
      .catch(function () {
        if (reqId !== self.reqId) return;
        self.results.classList.remove('is-loading');
        self.results.innerHTML =
          '<p class="hmc-smart-search__empty">Search unavailable. Try again.</p>';
      });
  };

  SmartSearch.prototype._renderResults = function (q, data) {
    this.results.classList.remove('is-loading');
    var products =
      (data &&
        data.resources &&
        data.resources.results &&
        data.resources.results.products) ||
      [];

    if (!products.length) {
      this.results.innerHTML =
        '<p class="hmc-smart-search__empty">No products found for “' +
        escapeHtml(q) +
        '”.</p>';
      return;
    }

    var html = '<ul class="hmc-smart-search__list" role="list">';
    products.forEach(function (p) {
      var img = p.featured_image && p.featured_image.url ? p.featured_image.url : p.image;
      html +=
        '<li class="hmc-smart-search__item">' +
        '<a class="hmc-smart-search__link" href="' +
        escapeHtml(p.url) +
        '">' +
        (img
          ? '<span class="hmc-smart-search__thumb"><img src="' +
            escapeHtml(img) +
            '" alt="" loading="lazy" width="56" height="56"></span>'
          : '<span class="hmc-smart-search__thumb hmc-smart-search__thumb--empty"></span>') +
        '<span class="hmc-smart-search__meta">' +
        '<span class="hmc-smart-search__name">' +
        highlightQuery(p.title, q) +
        '</span>' +
        (p.price ? '<span class="hmc-smart-search__price">' + formatMoney(p.price) + '</span>' : '') +
        '</span></a></li>';
    });
    html += '</ul>';

    var searchUrl =
      (window.routes && window.routes.searchUrl ? window.routes.searchUrl : '/search') +
      '?type=product&q=' +
      encodeURIComponent(q);
    html +=
      '<a class="hmc-smart-search__view-all" href="' +
      escapeHtml(searchUrl) +
      '">View all results <span aria-hidden="true">→</span></a>';

    this.results.innerHTML = html;
  };

  function boot() {
    document.querySelectorAll('.hmc-smart-search').forEach(function (el) {
      if (!el.__hmcSmartSearch) el.__hmcSmartSearch = new SmartSearch(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
